import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  User, Plus, X, Upload, Loader2, Edit3, Trash2, Image,
  CheckCircle2, DollarSign, Globe, ChevronDown,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SKILLS_SUGGESTIONS = ['AutoCAD','SketchUp','3ds Max','V-Ray','Blender','Photoshop','Illustrator','Project Management','On-site Supervision','Electrical Wiring','AV Systems','Rigging','Welding','CNC','Woodwork'];
const LANGUAGES = ['Türkçe','English','Deutsch','Français','Español','Italiano','Arabic','Russian','Dutch'];
const CURRENCIES = ['USD','EUR','GBP','TRY','AED'];

export default function FreelancerProfilePage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [newSkill, setNewSkill] = useState('');
  const [addingPortfolioItem, setAddingPortfolioItem] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', description: '', price: '', currency: 'USD' });
  const [uploadingImages, setUploadingImages] = useState({});
  const fileInputRef = useRef();
  const avatarInputRef = useRef();

  useEffect(() => {
    axios.get(`${API}/freelancer/profile`, { headers })
      .then(r => setProfile(r.data))
      .catch(() => toast.error('Profil yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  const updateField = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/freelancer/profile`, {
        headline: profile.headline,
        bio: profile.bio,
        skills: profile.skills,
        availability: profile.availability,
        daily_rate: profile.daily_rate ? parseFloat(profile.daily_rate) : null,
        daily_rate_currency: profile.daily_rate_currency,
        languages: profile.languages,
        website: profile.website,
        linkedin: profile.linkedin,
        instagram: profile.instagram,
        avatar_url: profile.avatar_url,
      }, { headers });
      toast.success('Profil kaydedildi!');
    } catch { toast.error('Kayıt başarısız'); }
    finally { setSaving(false); }
  };

  const uploadAvatar = async (file) => {
    setAvatarUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/single`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      const url = res.data?.url || res.data?.file_url || '';
      if (url) { updateField('avatar_url', url); toast.success('Fotoğraf yüklendi'); }
    } catch { toast.error('Fotoğraf yüklenemedi'); }
    finally { setAvatarUploading(false); }
  };

  const addPortfolioItem = async () => {
    if (!newItem.title.trim()) { toast.error('Başlık gerekli'); return; }
    try {
      const res = await axios.post(`${API}/freelancer/portfolio/items`, {
        title: newItem.title, description: newItem.description,
        price: newItem.price ? parseFloat(newItem.price) : null,
        currency: newItem.currency,
      }, { headers });
      setProfile(p => ({ ...p, portfolio: [...(p.portfolio || []), res.data.item] }));
      setNewItem({ title: '', description: '', price: '', currency: 'USD' });
      setAddingPortfolioItem(false);
      toast.success('Portfolyo öğesi eklendi');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Eklenemedi'); }
  };

  const deletePortfolioItem = async (itemId) => {
    try {
      await axios.delete(`${API}/freelancer/portfolio/items/${itemId}`, { headers });
      setProfile(p => ({ ...p, portfolio: p.portfolio.filter(i => i.id !== itemId) }));
      toast.success('Silindi');
    } catch { toast.error('Silinemedi'); }
  };

  const uploadPortfolioImage = async (itemId, file) => {
    setUploadingImages(u => ({ ...u, [itemId]: true }));
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/single`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      const url = res.data?.url || res.data?.file_url || '';
      if (url) {
        await axios.post(`${API}/freelancer/portfolio/items/${itemId}/images`, { image_url: url }, { headers });
        setProfile(p => ({
          ...p,
          portfolio: p.portfolio.map(item =>
            item.id === itemId ? { ...item, images: [...(item.images || []), url] } : item
          )
        }));
        toast.success('Görsel eklendi');
      }
    } catch { toast.error('Görsel yüklenemedi'); }
    finally { setUploadingImages(u => ({ ...u, [itemId]: false })); }
  };

  const removePortfolioImage = async (itemId, imageUrl) => {
    try {
      await axios.delete(`${API}/freelancer/portfolio/items/${itemId}/images`, { data: { image_url: imageUrl }, headers });
      setProfile(p => ({
        ...p,
        portfolio: p.portfolio.map(item =>
          item.id === itemId ? { ...item, images: item.images.filter(i => i !== imageUrl) } : item
        )
      }));
    } catch { toast.error('Silinemedi'); }
  };

  const addSkill = () => {
    const s = newSkill.trim();
    if (!s) return;
    if (!(profile.skills || []).includes(s)) updateField('skills', [...(profile.skills || []), s]);
    setNewSkill('');
  };
  const removeSkill = (s) => updateField('skills', (profile.skills || []).filter(x => x !== s));
  const toggleLanguage = (l) => {
    const langs = profile.languages || [];
    updateField('languages', langs.includes(l) ? langs.filter(x => x !== l) : [...langs, l]);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Profil & Portfolyo</h1>
          <p className="text-sm text-gray-500 mt-0.5">Profilini düzenle, işlerini sergile</p>
        </div>
        <Button onClick={saveProfile} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Kaydet
        </Button>
      </div>

      {/* Avatar + Basic Info */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
        <h3 className="font-bold text-gray-900 mb-4">Kişisel Bilgiler</h3>
        <div className="flex items-start gap-6 flex-wrap">
          {/* Avatar */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-20 h-20 rounded-2xl bg-gray-100 border-2 border-dashed border-gray-200 overflow-hidden flex items-center justify-center cursor-pointer relative"
              onClick={() => avatarInputRef.current?.click()}>
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt="avatar" className="w-full h-full object-cover" />
              ) : avatarUploading ? (
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              ) : (
                <User className="w-8 h-8 text-gray-300" />
              )}
            </div>
            <input ref={avatarInputRef} type="file" accept="image/*" className="hidden"
              onChange={e => e.target.files?.[0] && uploadAvatar(e.target.files[0])} />
            <button onClick={() => avatarInputRef.current?.click()}
              className="text-xs text-red-600 hover:underline font-medium">Fotoğraf Yükle</button>
          </div>

          {/* Fields */}
          <div className="flex-1 space-y-3 min-w-0">
            <input value={profile.headline || ''} onChange={e => updateField('headline', e.target.value)}
              placeholder="Başlık (örn: 3D Stand Designer | Dubai & İstanbul)"
              className="w-full border border-gray-200 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-400" />
            <textarea value={profile.bio || ''} onChange={e => updateField('bio', e.target.value)}
              placeholder="Kendini tanıt..." rows={3}
              className="w-full border border-gray-200 text-sm rounded-xl px-4 py-2.5 resize-none focus:outline-none focus:border-red-400" />
            <div className="grid grid-cols-2 gap-3">
              <input value={profile.website || ''} onChange={e => updateField('website', e.target.value)}
                placeholder="Website URL" className="border border-gray-200 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-400" />
              <input value={profile.linkedin || ''} onChange={e => updateField('linkedin', e.target.value)}
                placeholder="LinkedIn URL" className="border border-gray-200 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-400" />
            </div>
          </div>
        </div>

        {/* Rate + Availability */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="flex gap-2">
            <input type="number" min="0" value={profile.daily_rate || ''} onChange={e => updateField('daily_rate', e.target.value)}
              placeholder="Günlük ücret" className="flex-1 min-w-0 border border-gray-200 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-red-400" />
            <select value={profile.daily_rate_currency || 'USD'} onChange={e => updateField('daily_rate_currency', e.target.value)}
              className="border border-gray-200 text-sm rounded-xl px-2 py-2.5 bg-white focus:outline-none focus:border-red-400">
              {CURRENCIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <select value={profile.availability || 'available'} onChange={e => updateField('availability', e.target.value)}
            className="col-span-1 border border-gray-200 text-sm rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:border-red-400">
            <option value="available">Müsait</option>
            <option value="busy">Meşgul</option>
            <option value="unavailable">Müsait Değil</option>
          </select>
        </div>
      </div>

      {/* Skills */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
        <h3 className="font-bold text-gray-900 mb-3">Yetenekler</h3>
        <div className="flex flex-wrap gap-2 mb-3">
          {(profile.skills || []).map(s => (
            <span key={s} className="flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200 text-xs px-3 py-1.5 rounded-full font-medium">
              {s}<button onClick={() => removeSkill(s)} className="hover:text-red-900"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newSkill} onChange={e => setNewSkill(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addSkill()}
            placeholder="Yetenek ekle..." list="skills-list"
            className="flex-1 border border-gray-200 text-sm rounded-xl px-3 py-2 focus:outline-none focus:border-red-400" />
          <datalist id="skills-list">{SKILLS_SUGGESTIONS.map(s => <option key={s} value={s} />)}</datalist>
          <Button onClick={addSkill} size="sm" className="bg-red-600 hover:bg-red-700 text-white px-3">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Languages */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
        <h3 className="font-bold text-gray-900 mb-3">Diller</h3>
        <div className="flex flex-wrap gap-2">
          {LANGUAGES.map(l => {
            const active = (profile.languages || []).includes(l);
            return (
              <button key={l} onClick={() => toggleLanguage(l)}
                className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors
                  ${active ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-200 hover:border-red-300'}`}>
                {l}
              </button>
            );
          })}
        </div>
      </div>

      {/* Portfolio */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-gray-900">Portfolyo</h3>
            <p className="text-xs text-gray-400">{(profile.portfolio || []).length} / 20 proje</p>
          </div>
          <Button size="sm" onClick={() => setAddingPortfolioItem(true)}
            className="bg-red-600 hover:bg-red-700 text-white gap-1.5">
            <Plus className="w-4 h-4" />Proje Ekle
          </Button>
        </div>

        {/* Add new item form */}
        {addingPortfolioItem && (
          <div className="mb-4 p-4 bg-red-50 rounded-xl border border-red-100 space-y-3">
            <input value={newItem.title} onChange={e => setNewItem(p => ({ ...p, title: e.target.value }))}
              placeholder="Proje başlığı *" className="w-full border border-gray-200 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-red-400" />
            <textarea value={newItem.description} onChange={e => setNewItem(p => ({ ...p, description: e.target.value }))}
              placeholder="Açıklama (opsiyonel)" rows={2}
              className="w-full border border-gray-200 text-sm rounded-xl px-3 py-2.5 resize-none focus:outline-none focus:border-red-400" />
            <div className="flex gap-2">
              <input type="number" min="0" value={newItem.price} onChange={e => setNewItem(p => ({ ...p, price: e.target.value }))}
                placeholder="Fiyat (opsiyonel)" className="flex-1 border border-gray-200 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-red-400" />
              <select value={newItem.currency} onChange={e => setNewItem(p => ({ ...p, currency: e.target.value }))}
                className="border border-gray-200 text-sm rounded-xl px-2 py-2.5 bg-white">
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <Button onClick={addPortfolioItem} className="bg-red-600 hover:bg-red-700 text-white text-sm flex-1 gap-1">
                <CheckCircle2 className="w-4 h-4" />Ekle
              </Button>
              <Button variant="outline" onClick={() => setAddingPortfolioItem(false)} className="flex-1 text-sm">İptal</Button>
            </div>
          </div>
        )}

        {/* Portfolio items */}
        {(profile.portfolio || []).length === 0 ? (
          <div className="text-center py-8">
            <Briefcase className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">Henüz portfolyo öğesi yok</p>
            <p className="text-xs text-gray-300 mt-1">İlk projeni ekleyerek görünürlüğünü artır</p>
          </div>
        ) : (
          <div className="space-y-4">
            {(profile.portfolio || []).map(item => (
              <div key={item.id} className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                    {item.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.description}</p>}
                    {item.price && <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 rounded-full mt-1 inline-block">{item.price} {item.currency}</span>}
                  </div>
                  <button onClick={() => deletePortfolioItem(item.id)}
                    className="w-7 h-7 rounded-lg hover:bg-red-50 flex items-center justify-center text-gray-400 hover:text-red-600 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Images */}
                <div className="flex gap-2 flex-wrap">
                  {(item.images || []).map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img src={img} alt="" className="w-16 h-16 object-cover rounded-lg border border-gray-100" />
                      <button onClick={() => removePortfolioImage(item.id, img)}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white rounded-full hidden group-hover:flex items-center justify-center">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                  {(item.images || []).length < 6 && (
                    <label className="w-16 h-16 border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-red-400 transition-colors">
                      {uploadingImages[item.id] ? (
                        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                      ) : (
                        <>
                          <Plus className="w-4 h-4 text-gray-400" />
                          <span className="text-[9px] text-gray-400 mt-0.5">Görsel</span>
                        </>
                      )}
                      <input type="file" accept="image/*" className="hidden"
                        onChange={e => e.target.files?.[0] && uploadPortfolioImage(item.id, e.target.files[0])} />
                    </label>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Fix missing import
function Briefcase({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>;
}
